import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Page from "../components/layout/Page";
import { blogPosts as blogPostsService } from "../services/api";

// Blog post data interface
interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  meta_title: string;
  meta_description: string;
  author: {
    name: string;
    email: string;
  };
  category: {
    name: string;
    slug: string;
    color?: string;
  };
  tags: {
    id: number;
    name: string;
    slug: string;
    color?: string;
  }[];
  featured_image?: string;
  featured_image_alt?: string;
  is_featured: boolean;
  views_count: number;
  reading_time_minutes?: number;
  published_at: string;
  created_at: string;
  updated_at: string;
}

// Placeholder colors for categories and tags
const getCategoryColor = (categorySlug: string): string => {
  const colors: { [key: string]: string } = {
    technology: "#007bff",
    tutorial: "#28a745",
    commercial: "#ffc107",
    sustainability: "#17a2b8",
    security: "#dc3545",
    entertainment: "#fd7e14",
  };
  return colors[categorySlug] || "#6c757d";
};

const getTagColor = (tagSlug: string): string => {
  const colors: { [key: string]: string } = {
    "smart-home": "#007bff",
    automation: "#28a745",
    iot: "#17a2b8",
    loxone: "#fd7e14",
    guide: "#6c757d",
  };
  return colors[tagSlug] || "#6c757d";
};

export default function BlogPostView() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRelatedPosts = async (categorySlug: string) => {
      try {
        const response = (await blogPostsService.list({
          category: categorySlug,
          limit: 6,
        })) as any;

        if (response && response.posts) {
          // Filter out the current post and limit to 3
          const related = response.posts
            .filter((p: any) => p.slug !== slug)
            .slice(0, 3)
            .map((p: any) => ({
              ...p,
              author: {
                name: p.author_name || p.author?.name || "Unknown",
                email: p.author_email || p.author?.email || "",
              },
              category: {
                name: p.category_name || p.category?.name || "Uncategorized",
                slug: p.category_slug || p.category?.slug || "",
                color:
                  p.category?.color ||
                  p.category_color ||
                  getCategoryColor(p.category_slug || p.category?.slug || ""),
              },
              tags: Array.isArray(p.tags)
                ? p.tags.map((tag: any) => ({
                    id: tag.id,
                    name: tag.name,
                    slug: tag.slug,
                    color: tag.color || getTagColor(tag.slug),
                  }))
                : [],
              reading_time_minutes: p.reading_time_minutes || 5,
            }));
          setRelatedPosts(related);
        }
      } catch (error) {
        console.error("Error loading related posts:", error);
      }
    };

    const loadPost = async () => {
      try {
        setLoading(true);

        // Load the specific post by slug
        const response = (await blogPostsService.get(slug || "")) as any;
        console.log("Blog post response:", response);

        if (response && response.post) {
          const fetchedPost = response.post;
          // Transform backend response to match interface
          const transformedPost = {
            ...fetchedPost,
            author: {
              name:
                fetchedPost.author_name ||
                fetchedPost.author?.name ||
                "Unknown",
              email:
                fetchedPost.author_email || fetchedPost.author?.email || "",
            },
            category: {
              name:
                fetchedPost.category_name ||
                fetchedPost.category?.name ||
                "Uncategorized",
              slug:
                fetchedPost.category_slug || fetchedPost.category?.slug || "",
              color:
                fetchedPost.category?.color ||
                fetchedPost.category_color ||
                getCategoryColor(
                  fetchedPost.category_slug || fetchedPost.category?.slug || ""
                ),
            },
            tags: Array.isArray(fetchedPost.tags)
              ? fetchedPost.tags.map((tag: any) => ({
                  id: tag.id,
                  name: tag.name,
                  slug: tag.slug,
                  color: tag.color || getTagColor(tag.slug),
                }))
              : [],
            reading_time_minutes: fetchedPost.reading_time_minutes || 5,
          };
          setPost(transformedPost);

          // Load related posts from the same category
          await loadRelatedPosts(transformedPost.category.slug);
        }
      } catch (error) {
        console.error("Error loading blog post:", error);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ro-RO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Page>
        <div className="blog-post-loading">
          <div className="loading-spinner"></div>
          <p>Loading article...</p>
        </div>
      </Page>
    );
  }

  if (!post) {
    return (
      <Page>
        <div className="blog-post-not-found">
          <h1>Article not found</h1>
          <p>The article you're looking for could not be found.</p>
          <Link to="/blog" className="btn btn-primary">
            <i className="fa-solid fa-arrow-left"></i> Back to Blog
          </Link>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <Helmet>
        <title>{post.meta_title || post.title}</title>
        <meta
          name="description"
          content={post.meta_description || post.excerpt}
        />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta
          property="og:description"
          content={post.meta_description || post.excerpt}
        />
        {post.featured_image && (
          <meta property="og:image" content={post.featured_image} />
        )}
        <meta property="og:type" content="article" />
        <meta
          name="keywords"
          content={post.tags.map((t) => t.name).join(", ")}
        />
        <meta name="author" content={post.author.name} />
      </Helmet>
      <article className="blog-post">
        {/* Hero Section with Background Image */}
        <div
          className="blog-post-hero"
          style={{
            backgroundImage: post.featured_image
              ? `linear-gradient(135deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${post.featured_image}')`
              : "linear-gradient(135deg, #1a5b4a, #2d8659)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            minHeight: "500px",
            display: "flex",
            alignItems: "flex-end",
            padding: "3rem 2rem 2rem 2rem",
          }}
        >
          <div className="hero-content">
            <div className="post-meta">
              <Link
                to={`#`}
                className="category-badge"
                style={{ backgroundColor: post.category.color }}
              >
                {post.category.name}
              </Link>
              <span className="reading-time">
                <i className="fa-solid fa-clock"></i>
                {post.reading_time_minutes} min read
              </span>
            </div>

            <h1 className="post-title">{post.title}</h1>
            <p className="post-excerpt">{post.excerpt}</p>
          </div>
        </div>

        {/* Content */}
        <div className="blog-post-content">
          <div className="content-wrapper">
            <div
              className="post-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {/* <div className="post-tags">
              <h4>Tags:</h4>
              <div className="tags-list">
                {post.tags.map((tag) => (
                  <Link
                    key={tag.id}
                    to={`/blog/tag/${tag.slug}`}
                    className="tag"
                    style={{ borderColor: tag.color, color: tag.color }}
                  >
                    {tag.name}
                  </Link>
                ))}
              </div>
            </div> */}

            {/* Share Buttons */}
            {/* <div className="post-share">
              <h4>Share this article:</h4>
              <div className="share-buttons">
                <button
                  onClick={() => {
                    const url = window.location.href;
                    const text = `${post.title} - ${post.excerpt}`;
                    navigator.share?.({ title: post.title, text, url }) ||
                      window.open(
                        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                          url
                        )}`
                      );
                  }}
                  className="share-btn facebook"
                >
                  <i className="fa-brands fa-facebook-f"></i>
                  Facebook
                </button>
                <button
                  onClick={() => {
                    const url = window.location.href;
                    const text = `${post.title} - ${post.excerpt}`;
                    window.open(
                      `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                        text
                      )}&url=${encodeURIComponent(url)}`
                    );
                  }}
                  className="share-btn twitter"
                >
                  <i className="fa-brands fa-twitter"></i>
                  Twitter
                </button>
                <button
                  onClick={() => {
                    const url = window.location.href;
                    const text = `${post.title}\\n\\n${post.excerpt}\\n\\n${url}`;
                    window.open(
                      `https://wa.me/?text=${encodeURIComponent(text)}`
                    );
                  }}
                  className="share-btn whatsapp"
                >
                  <i className="fa-brands fa-whatsapp"></i>
                  WhatsApp
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    alert("Link copied to clipboard!");
                  }}
                  className="share-btn copy"
                >
                  <i className="fa-solid fa-link"></i>
                  Copy Link
                </button>
              </div>
            </div> */}
          </div>

          {/* Sidebar */}
          <aside className="blog-sidebar">
            {relatedPosts.length > 0 && (
              <div className="sidebar-section">
                <h3>Articole Similare</h3>
                <div className="related-posts">
                  {relatedPosts.map((relatedPost) => (
                    <Link
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.slug}`}
                      className="related-post"
                    >
                      {relatedPost.featured_image && (
                        <img
                          src={relatedPost.featured_image}
                          alt={relatedPost.featured_image_alt}
                        />
                      )}
                      <div className="related-content">
                        <span
                          className="related-category"
                          style={{ color: relatedPost.category.color }}
                        >
                          {relatedPost.category.name}
                        </span>
                        <h4>{relatedPost.title}</h4>
                        <span className="related-date">
                          {formatDate(relatedPost.published_at)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="sidebar-section">
              <h3 className="mb-0">HAI SĂ DISCUTĂM PROIECTUL TĂU</h3>
              <div className="contact-card">
                <p className="text-left left" style={{ textAlign: "left" }}>
                  Spune-ne ce îți dorești pentru casa sau clădirea ta, iar
                  echipa ASEMPERA îți va arăta cum tehnologia Loxone și
                  experiența noastră de peste 20 de ani în automatizări pot
                  aduce viață planurilor tale.
                </p>
                <Link
                  to="/#contact"
                  className="special-button button-special small w-full w-100"
                >
                  <i className="fa-solid fa-phone"></i>
                  Contactează-ne
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </article>
    </Page>
  );
}
